import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/**
 * Formulaire partagé Création + Modification d'un propriétaire.
 * Toggle Particulier/Professionnel en tête : si "professionnel", champs raison
 * sociale + SIRET + TVA requis avant validation.
 *
 * Props :
 *  - data : objet d'état (forme {owner_type, first_name, last_name, ...})
 *  - setData : setter React standard (s => ({ ...s, ... }))
 *  - mode : "create" | "edit" — petits ajustements UI
 *  - emailField : noeud React optionnel pour personnaliser le champ email
 *                 (créer = input simple, modifier = readonly + bouton Changer)
 */
export default function OwnerForm({ data, setData, mode = "create", emailField = null }) {
  const isPro = data.owner_type === "professionnel";

  const addContact = () =>
    setData((s) => ({
      ...s,
      contacts: [...(s.contacts || []), { email: "", role: "operational" }],
    }));

  const updateContact = (idx, patch) =>
    setData((s) => {
      const copy = [...(s.contacts || [])];
      copy[idx] = { ...copy[idx], ...patch };
      return { ...s, contacts: copy };
    });

  const removeContact = (idx) => {
    // Le premier contact (role="all") est verrouillé — synchronisé avec le login.
    if (idx === 0) return;
    setData((s) => {
      const copy = [...(s.contacts || [])];
      copy.splice(idx, 1);
      return { ...s, contacts: copy };
    });
  };

  // Le premier contact reflète toujours l'email de login (billing_email).
  // Son RÔLE est éditable (par défaut "all") — l'admin peut choisir "billing"
  // ou "operational". La validation garantit qu'au moins un contact reçoit chaque
  // type d'email (factures / opérationnel).
  const primaryContact = (() => {
    const arr = data.contacts || [];
    if (arr.length > 0 && arr[0]?.email) return arr[0];
    return { email: (data.billing_email || "").trim().toLowerCase(), role: "all" };
  })();
  const additionalContacts = ((data.contacts || []).length > 0)
    ? (data.contacts || []).slice(1)
    : [];

  const setAddrStructured = (patch) =>
    setData((s) => ({
      ...s,
      billing_address_structured: { ...(s.billing_address_structured || {}), ...patch },
    }));

  return (
    <div data-testid="owner-form" className="space-y-3">
      {/* TOGGLE TYPE — Particulier / Professionnel */}
      <div data-testid="owner-form-type-section" className="rounded-xl border border-white/10 bg-white/[0.08] p-3">
        <Label className="text-sm text-slate-50 mb-2 block">Type de propriétaire</Label>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="owner-form-type-particulier"
            onClick={() => setData((s) => ({ ...s, owner_type: "particulier" }))}
            className={`flex-1 rounded-full px-4 py-2 text-sm font-medium transition-colors border ${
              !isPro
                ? "bg-violet-500 text-white border-violet-500"
                : "bg-white/[0.04] text-slate-200 border-white/10 hover:bg-white/[0.10]"
            }`}
          >
            👤 Particulier
          </button>
          <button
            type="button"
            data-testid="owner-form-type-professionnel"
            onClick={() => setData((s) => ({ ...s, owner_type: "professionnel" }))}
            className={`flex-1 rounded-full px-4 py-2 text-sm font-medium transition-colors border ${
              isPro
                ? "bg-violet-500 text-white border-violet-500"
                : "bg-white/[0.04] text-slate-200 border-white/10 hover:bg-white/[0.10]"
            }`}
          >
            🏢 Professionnel
          </button>
        </div>
      </div>

      {/* Bloc PRO — visible uniquement si type=professionnel */}
      {isPro && (
        <div data-testid="owner-form-pro-block" className="rounded-xl border border-amber-400/30 bg-amber-500/[0.06] p-3 space-y-2">
          <div className="text-xs text-amber-200 mb-1">Champs requis pour un professionnel</div>
          <div className="space-y-1">
            <Label data-testid="owner-form-raison-sociale-label">Raison sociale *</Label>
            <Input
              data-testid="owner-form-raison-sociale-input"
              className="border-white/10 bg-white/[0.08]"
              value={data.raison_sociale || ""}
              onChange={(e) => setData((s) => ({ ...s, raison_sociale: e.target.value }))}
              placeholder="Nom de la société"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label data-testid="owner-form-siret-label">SIRET *</Label>
              <Input
                data-testid="owner-form-siret-input"
                className="border-white/10 bg-white/[0.08]"
                value={data.siret || ""}
                onChange={(e) => setData((s) => ({ ...s, siret: e.target.value.replace(/\D/g, "").slice(0, 14) }))}
                placeholder="14 chiffres"
                inputMode="numeric"
              />
            </div>
            <div className="space-y-1">
              <Label data-testid="owner-form-tva-label">TVA intracommunautaire</Label>
              <Input
                data-testid="owner-form-tva-input"
                className="border-white/10 bg-white/[0.08]"
                value={data.tva_intracommunautaire || ""}
                onChange={(e) => setData((s) => ({ ...s, tva_intracommunautaire: e.target.value }))}
                placeholder="FRXX XXX XXX XXX"
              />
            </div>
          </div>
        </div>
      )}

      {/* IDENTITÉ */}
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label data-testid="owner-form-lastname-label">{isPro ? "Nom du contact" : "Nom"}</Label>
          <Input
            data-testid="owner-form-lastname-input"
            className="border-white/10 bg-white/[0.08]"
            value={data.last_name || ""}
            onChange={(e) => setData((s) => ({ ...s, last_name: e.target.value }))}
          />
        </div>
        <div className="space-y-1">
          <Label data-testid="owner-form-firstname-label">{isPro ? "Prénom du contact" : "Prénom"}</Label>
          <Input
            data-testid="owner-form-firstname-input"
            className="border-white/10 bg-white/[0.08]"
            value={data.first_name || ""}
            onChange={(e) => setData((s) => ({ ...s, first_name: e.target.value }))}
          />
        </div>
      </div>

      {/* EMAIL (perso ou délégué via emailField) */}
      {emailField ? (
        emailField
      ) : (
        <div className="space-y-1">
          <Label data-testid="owner-form-email-label">E-mail (login + contact facturation par défaut)</Label>
          <Input
            data-testid="owner-form-email-input"
            type="email"
            className="border-white/10 bg-white/[0.08]"
            value={data.billing_email || ""}
            onChange={(e) => setData((s) => ({ ...s, billing_email: e.target.value }))}
          />
        </div>
      )}

      <div className="space-y-1">
        <Label data-testid="owner-form-phone-label">Téléphone</Label>
        <Input
          data-testid="owner-form-phone-input"
          className="border-white/10 bg-white/[0.08]"
          value={data.phone || ""}
          onChange={(e) => setData((s) => ({ ...s, phone: e.target.value }))}
        />
      </div>

      {/* LANGUE DE COMMUNICATION (emails admin → propriétaire) */}
      <div className="space-y-1">
        <Label data-testid="owner-form-language-label">Langue de communication</Label>
        <select
          data-testid="owner-form-language-select"
          className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
          value={data.language || "fr"}
          onChange={(e) => setData((s) => ({ ...s, language: e.target.value }))}
        >
          <option value="fr">Français</option>
          <option value="en">English</option>
        </select>
      </div>

      {/* ADRESSE DE FACTURATION (legacy text + structurée optionnelle) */}
      <div className="rounded-xl border border-white/10 bg-white/[0.08] p-3 space-y-2">
        <div className="text-sm text-slate-50">Adresse de facturation</div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <Input
            data-testid="owner-form-addr-street"
            placeholder="Rue"
            className="md:col-span-3 border-white/10 bg-white/[0.08]"
            value={data.billing_address_structured?.street || ""}
            onChange={(e) => setAddrStructured({ street: e.target.value })}
          />
          <Input
            data-testid="owner-form-addr-postal"
            placeholder="Code postal"
            className="border-white/10 bg-white/[0.08]"
            value={data.billing_address_structured?.postal_code || ""}
            onChange={(e) => setAddrStructured({ postal_code: e.target.value })}
          />
          <Input
            data-testid="owner-form-addr-city"
            placeholder="Ville"
            className="md:col-span-2 border-white/10 bg-white/[0.08]"
            value={data.billing_address_structured?.city || ""}
            onChange={(e) => setAddrStructured({ city: e.target.value })}
          />
        </div>
        <Textarea
          data-testid="owner-form-billing-address-textarea"
          className="border-white/10 bg-white/[0.08] text-xs"
          placeholder="Adresse libre (utilisée si la version structurée est vide)"
          value={data.billing_address || ""}
          onChange={(e) => setData((s) => ({ ...s, billing_address: e.target.value }))}
        />
      </div>

      {/* CONTACTS multi-rôles */}
      <div className="rounded-xl border border-white/10 bg-white/[0.08] p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-50">Contacts</div>
          <Button
            data-testid="owner-form-add-contact"
            type="button"
            variant="outline"
            size="sm"
            className="border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10"
            onClick={addContact}
          >
            + Ajouter contact
          </Button>
        </div>
        <div className="text-xs text-slate-400">
          Le premier contact reste synchronisé avec l&apos;e-mail de login. Son rôle est modifiable :
          choisissez qui reçoit les factures et qui reçoit les notifications opérationnelles.
        </div>
        <div className="space-y-2">
          {/* Ligne 0 : contact principal (verrouillé, rôle=all, miroir du billing_email) */}
          <div data-testid="owner-contact-row-0" className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              data-testid="owner-contact-email-0"
              type="email"
              readOnly
              className="flex-1 border-white/10 bg-white/[0.05] text-slate-300 cursor-not-allowed"
              value={primaryContact.email}
              title="Synchronisé avec l'e-mail de login — modifier via le champ « E-mail (login) » plus haut."
            />
            <select
              data-testid="owner-contact-role-0"
              className="flex h-9 w-full sm:w-[220px] rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
              value={primaryContact.role || "all"}
              onChange={(e) => {
                const newRole = e.target.value;
                setData((s) => {
                  const arr = [...(s.contacts || [])];
                  if (arr.length === 0) {
                    arr.push({ email: primaryContact.email, role: newRole });
                  } else {
                    arr[0] = { ...arr[0], email: primaryContact.email, role: newRole };
                  }
                  return { ...s, contacts: arr };
                });
              }}
            >
              <option value="all">Tous</option>
              <option value="billing">Factures uniquement</option>
              <option value="operational">Opérationnel &amp; Incidents</option>
            </select>
            <Button
              data-testid="owner-contact-remove-0"
              type="button"
              variant="outline"
              size="sm"
              disabled
              className="border-white/10 bg-white/[0.03] text-slate-500 cursor-not-allowed"
              title="Le contact principal ne peut pas être supprimé — il correspond à l'e-mail de login."
            >
              ✕
            </Button>
          </div>

          {/* Lignes 1+ : contacts additionnels éditables */}
          {additionalContacts.map((c, i) => {
            const realIdx = i + 1; // index dans le tableau contacts complet (0 = primary)
            return (
              <div key={realIdx} data-testid={`owner-contact-row-${realIdx}`} className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Input
                  data-testid={`owner-contact-email-${realIdx}`}
                  type="email"
                  placeholder="email@exemple.com"
                  className="flex-1 border-white/10 bg-white/[0.08]"
                  value={c.email || ""}
                  onChange={(e) => updateContact(realIdx, { email: e.target.value })}
                />
                <select
                  data-testid={`owner-contact-role-${realIdx}`}
                  className="flex h-9 w-full sm:w-[220px] rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
                  value={c.role || "operational"}
                  onChange={(e) => updateContact(realIdx, { role: e.target.value })}
                >
                  <option value="all">Tous</option>
                  <option value="billing">Factures uniquement</option>
                  <option value="operational">Opérationnel &amp; Incidents</option>
                </select>
                <Button
                  data-testid={`owner-contact-remove-${realIdx}`}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-rose-500/30 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20"
                  onClick={() => removeContact(realIdx)}
                >
                  ✕
                </Button>
              </div>
            );
          })}
          {additionalContacts.length === 0 && (
            <div data-testid="owner-contact-empty-hint" className="text-xs text-slate-400 italic">
              Aucun contact additionnel. Utilisez « + Ajouter contact » pour envoyer les emails à d&apos;autres personnes (comptable, gestionnaire, etc.).
            </div>
          )}
        </div>
      </div>

      {/* NOTES */}
      <div className="space-y-1">
        <Label data-testid="owner-form-notes-label">Observation / note</Label>
        <Textarea
          data-testid="owner-form-notes-textarea"
          className="border-white/10 bg-white/[0.08]"
          value={data.notes || ""}
          onChange={(e) => setData((s) => ({ ...s, notes: e.target.value }))}
        />
      </div>

      {/* MOT DE PASSE INITIAL (uniquement création) */}
      {mode === "create" && (
        <div className="space-y-1">
          <Label data-testid="owner-form-password-label">Mot de passe initial</Label>
          <Input
            data-testid="owner-form-password-input"
            type="password"
            className="border-white/10 bg-white/[0.08]"
            value={data.initial_password || ""}
            onChange={(e) => setData((s) => ({ ...s, initial_password: e.target.value }))}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Validation côté frontend du payload owner avant POST/PUT.
 * Retourne null si OK, sinon une string décrivant le 1er problème.
 */
export function validateOwnerPayload(data, { mode = "create" } = {}) {
  if (!data.first_name?.trim() || !data.last_name?.trim()) {
    return "Nom et prénom du contact requis.";
  }
  if (!data.billing_email?.trim()) {
    return "E-mail requis.";
  }
  if (data.owner_type === "professionnel") {
    if (!data.raison_sociale?.trim()) return "Raison sociale requise pour un professionnel.";
    if (!data.siret || !/^\d{14}$/.test(data.siret)) {
      return "SIRET requis (14 chiffres) pour un professionnel.";
    }
  }
  if (mode === "create" && (!data.initial_password || data.initial_password.length < 8)) {
    return "Mot de passe initial (min. 8 caractères) requis.";
  }
  // Validation des contacts additionnels : email valide obligatoire dès qu'une ligne est présente.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const allContacts = data.contacts || [];
  const additional = allContacts.slice(1); // ligne 0 (email = billing_email) exclue
  for (let i = 0; i < additional.length; i++) {
    const c = additional[i] || {};
    const email = (c.email || "").trim();
    if (!email) {
      return `Contact additionnel n°${i + 1} : e-mail requis.`;
    }
    if (!EMAIL_RE.test(email)) {
      return `Contact additionnel n°${i + 1} : e-mail invalide.`;
    }
  }
  // Validation de couverture : au moins un contact doit recevoir les factures
  // et au moins un contact doit recevoir les emails opérationnels.
  // (rôles "all" = reçoit les deux ; "billing" = factures ; "operational" = ops)
  //
  // IMPORTANT — Contact primaire implicite : la UI affiche un contact primaire
  // par défaut {email: billing_email, role: "all"} dérivé à la volée (voir
  // `primaryContact` dans OwnerForm), MAIS il n'est persisté dans data.contacts
  // que si l'admin modifie explicitement son rôle. À la création (contacts=[]),
  // le backend applique le même défaut via `ensure_primary_all_contact`. On
  // reconstitue ce contact primaire pour que la validation frontend reflète
  // fidèlement l'état qui sera enregistré côté backend — sinon la création
  // échoue avec "Au moins un contact doit recevoir les factures" alors que
  // le contact primaire "Tous" est bien présent visuellement.
  const effectiveContacts = (() => {
    const arr = (allContacts || []).filter((c) => c?.email && c.email.trim());
    const billingEmail = (data.billing_email || "").trim().toLowerCase();
    if (arr.length === 0 && billingEmail) {
      return [{ email: billingEmail, role: "all" }];
    }
    return arr;
  })();
  const roles = effectiveContacts.map((c) => c.role);
  const receivesInvoice = roles.some((r) => r === "all" || r === "billing");
  const receivesOperational = roles.some((r) => r === "all" || r === "operational");
  if (!receivesInvoice) {
    return "Au moins un contact doit recevoir les factures.";
  }
  if (!receivesOperational) {
    return "Au moins un contact doit recevoir les notifications opérationnelles.";
  }
  return null;
}

/**
 * Construit le payload envoyé à l'API (POST /admin/owners ou PUT). Garantit
 * que les champs optionnels vides sont bien null et que les contacts sont
 * filtrés (lignes vides ignorées).
 */
export function buildOwnerPayload(data, { mode = "create" } = {}) {
  // Envoie l'intégralité des contacts (ligne 0 incluse) au backend.
  // Le backend `ensure_primary_all_contact` garantit que contacts[0].email = billing_email
  // en préservant le rôle choisi par l'admin.
  const arr = data.contacts || [];
  const contacts = arr
    .filter((c) => c?.email && c.email.trim())
    .map((c) => ({ email: c.email.trim().toLowerCase(), role: c.role || "operational" }));
  const addrS = data.billing_address_structured || {};
  const billing_address_structured =
    addrS.street || addrS.postal_code || addrS.city
      ? {
          street: addrS.street || null,
          postal_code: addrS.postal_code || null,
          city: addrS.city || null,
        }
      : null;
  const payload = {
    owner_type: data.owner_type || "particulier",
    first_name: data.first_name.trim(),
    last_name: data.last_name.trim(),
    phone: data.phone?.trim() || null,
    billing_email: data.billing_email.trim(),
    billing_address: data.billing_address?.trim() || null,
    billing_address_structured,
    raison_sociale: data.raison_sociale?.trim() || null,
    siret: data.siret?.trim() || null,
    tva_intracommunautaire: data.tva_intracommunautaire?.trim() || null,
    contacts,
    secondary_contact: {
      name: data.secondary_contact_name || null,
      phone: data.secondary_contact_phone || null,
      email: data.secondary_contact_email || null,
    },
    notes: data.notes?.trim() || null,
    language: data.language === "en" ? "en" : "fr",
    initial_password: mode === "create" ? (data.initial_password || "") : "unchanged",
  };
  return payload;
}
